import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FServiceAPI } from '@freelog/tools-lib';
import { decompile } from '@freelog/nmr_translator';
import { ruleMatchAndResult } from '@/models/informalNodeManagerPage';
import type { IRules, RuleMatchAndResultReturn } from '@/models/informalNodeManagerPage';
import fMessage from '@/components/fMessage';
import { mergeRules } from './helpers';
import type {
  ChangeAction,
  FetchInformalExhibitInfoAction,
  InformExhibitInfoPageModelState,
  OnHandleAttrModalAction,
  OnClick_Side_Exhibit_OnlyEditAttrInputOrSelect_ResetBtn_Action,
  OnChange_Side_Exhibit_OnlyEditAttrSelect_Action,
  OnBlur_Side_Exhibit_OnlyEditAttrSelect_Action,
  OnChange_Side_Exhibit_OnlyEditAttrInput_Action,
  OnBlur_Side_Exhibit_OnlyEditAttrInput_Action,
  OnChange_Side_Exhibit_EditDeleteAttrInput_Action,
  OnBlur_Side_Exhibit_EditDeleteAttrInput_Action,
  OnClick_Side_Exhibit_EditDeleteAttr_DeleteBtn_Action,
} from './types';

export const attrEffects = {
  * onHandleAttrModal({ payload }: OnHandleAttrModalAction, { select, put }: EffectsCommandMap) {
    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    if (payload.type === 'add') {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          side_CustomOptionsDrawer_Visible: true,
        },
      });
    } else {
      const attrT = informExhibitInfoPage.side_Exhibit_EditDeleteAttrs.find((ea) => {
        return ea.theKey === payload.theKey;
      });
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          side_CustomOptionDrawer_Visible: true,
          side_CustomOptionDrawer_DataSource: {
            key: attrT?.theKey || '',
            value: attrT?.theValue || '',
            description: attrT?.remark || '',
          },
        },
      });
    }
  },
  * onClick_Side_Exhibit_OnlyEditAttrInputOrSelect_ResetBtn({ payload }: OnClick_Side_Exhibit_OnlyEditAttrInputOrSelect_ResetBtn_Action, {
    select,
    call,
    put,
  }: EffectsCommandMap) {
    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informExhibitInfoPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    needHandleRules = needHandleRules.map((nhr) => {
      if (nhr.exhibitName === informExhibitInfoPage.exhibit_Name && nhr.operation !== 'activate_theme') {
        return {
          ...nhr,
          actions: nhr.actions.filter((a) => {
            return !((a.operation === 'add_attr' || a.operation === 'delete_attr') && a.content.key === payload.theKey);
          }),
        };
      }
      return nhr;
    });
    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informExhibitInfoPage.node_ID,
      testRuleText: text,
    };
    const { data, errCode, ret, msg }: {
      data: boolean;
      errCode: number;
      msg: string;
      ret: number;
    } = yield call(FServiceAPI.InformalNode.createRules, params);

    if (ret !== 0 || errCode !== 0 || !data) {
      return fMessage(msg, 'error');
    }
    fMessage('自定义选项已重置');

    const params2: Parameters<typeof ruleMatchAndResult>[0] = {
      nodeID: informExhibitInfoPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      fMessage('规则匹配失败');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
      },
    });

    yield put<FetchInformalExhibitInfoAction>({
      type: 'fetchInformalExhibitInfo',
    });
  },
  * onChange_Side_Exhibit_OnlyEditAttrSelect({ payload }: OnChange_Side_Exhibit_OnlyEditAttrSelect_Action, {
    select,
    put,
  }: EffectsCommandMap) {
    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    const theValue: string = payload.theValue;
    const textError: string = (theValue.length > 30 || theValue === '') ? '1~30个字符' : '';

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        side_Exhibit_OnlyEditAttrs: informExhibitInfoPage.side_Exhibit_OnlyEditAttrs.map<InformExhibitInfoPageModelState['side_Exhibit_OnlyEditAttrs'][number]>((poe) => {
          if (poe.theKey !== payload.theKey) {
            return poe;
          }
          return {
            ...poe,
            theValue: theValue,
            theValueError: textError,
          };
        }),
      },
    });
  },
  * onBlur_Side_Exhibit_OnlyEditAttrSelect({ payload }: OnBlur_Side_Exhibit_OnlyEditAttrSelect_Action, {
    select,
    call,
    put,
  }: EffectsCommandMap) {
    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informExhibitInfoPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    needHandleRules = needHandleRules.map((nhr) => {
      if (nhr.exhibitName === informExhibitInfoPage.exhibit_Name && nhr.operation !== 'activate_theme') {
        return {
          ...nhr,
          actions: nhr.actions.filter((a) => {
            return !((a.operation === 'add_attr' || a.operation === 'delete_attr') && a.content.key === payload.theKey);
          }),
        };
      }
      return nhr;
    });

    needHandleRules = mergeRules({
      oldRules: needHandleRules,
      exhibitName: informExhibitInfoPage.exhibit_Name,
      action: {
        operation: 'add_attr',
        content: {
          key: payload.theKey,
          value: payload.theValue,
          description: payload.theDescription,
        },
      },
    });
    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informExhibitInfoPage.node_ID,
      testRuleText: text,
    };
    const { data, errCode, ret, msg }: {
      data: boolean;
      errCode: number;
      msg: string;
      ret: number;
    } = yield call(FServiceAPI.InformalNode.createRules, params);

    if (ret !== 0 || errCode !== 0 || !data) {
      return fMessage(msg, 'error');
    }
    fMessage('自定义选项已更新');

    const params2: Parameters<typeof ruleMatchAndResult>[0] = {
      nodeID: informExhibitInfoPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      fMessage('规则匹配失败');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
      },
    });

    yield put<FetchInformalExhibitInfoAction>({
      type: 'fetchInformalExhibitInfo',
    });
  },

  * onChange_Side_Exhibit_OnlyEditAttrInput({ payload }: OnChange_Side_Exhibit_OnlyEditAttrInput_Action, {
    select,
    put,
  }: EffectsCommandMap) {
    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    const theValue: string = payload.theValue;
    const textError: string = theValue.length > 30 ? '1~30个字符' : '';

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        side_Exhibit_OnlyEditAttrs: informExhibitInfoPage.side_Exhibit_OnlyEditAttrs.map<InformExhibitInfoPageModelState['side_Exhibit_OnlyEditAttrs'][number]>((poe) => {
          if (poe.theKey !== payload.theKey) {
            return poe;
          }
          return {
            ...poe,
            theValue: theValue,
            theValueError: textError,
          };
        }),
      },
    });
  },
  * onBlur_Side_Exhibit_OnlyEditAttrInput({ payload }: OnBlur_Side_Exhibit_OnlyEditAttrInput_Action, {
    select,
    call,
    put,
  }: EffectsCommandMap) {
    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informExhibitInfoPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    needHandleRules = needHandleRules.map((nhr) => {
      if (nhr.exhibitName === informExhibitInfoPage.exhibit_Name && nhr.operation !== 'activate_theme') {
        return {
          ...nhr,
          actions: nhr.actions.filter((a) => {
            return !((a.operation === 'add_attr' || a.operation === 'delete_attr') && a.content.key === payload.theKey);
          }),
        };
      }
      return nhr;
    });

    needHandleRules = mergeRules({
      oldRules: needHandleRules,
      exhibitName: informExhibitInfoPage.exhibit_Name,
      action: {
        operation: 'add_attr',
        content: {
          key: payload.theKey,
          value: payload.theValue,
          description: payload.theDescription,
        },
      },
    });
    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informExhibitInfoPage.node_ID,
      testRuleText: text,
    };
    const { data, errCode, ret, msg }: {
      data: boolean;
      errCode: number;
      msg: string;
      ret: number;
    } = yield call(FServiceAPI.InformalNode.createRules, params);

    if (ret !== 0 || errCode !== 0 || !data) {
      return fMessage(msg, 'error');
    }
    fMessage('自定义选项已更新');

    const params2: Parameters<typeof ruleMatchAndResult>[0] = {
      nodeID: informExhibitInfoPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      fMessage('规则匹配失败');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
      },
    });

    yield put<FetchInformalExhibitInfoAction>({
      type: 'fetchInformalExhibitInfo',
    });
  },
  * onChange_Side_Exhibit_EditDeleteAttrInput({ payload }: OnChange_Side_Exhibit_EditDeleteAttrInput_Action, {
    select,
    call,
    put,
  }: EffectsCommandMap) {
    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    const theValue: string = payload.theValue;
    const textError: string = theValue.length > 30 ? '1~30个字符' : '';

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        side_Exhibit_EditDeleteAttrs: informExhibitInfoPage.side_Exhibit_EditDeleteAttrs.map<InformExhibitInfoPageModelState['side_Exhibit_EditDeleteAttrs'][number]>((poe) => {
          if (poe.theKey !== payload.theKey) {
            return poe;
          }
          return {
            ...poe,
            theValue: theValue,
            theValueError: textError,
          };
        }),
      },
    });
  },
  * onBlur_Side_Exhibit_EditDeleteAttrInput({ payload }: OnBlur_Side_Exhibit_EditDeleteAttrInput_Action, {
    select,
    call,
    put,
  }: EffectsCommandMap) {

    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informExhibitInfoPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    needHandleRules = needHandleRules.map((nhr) => {
      if (nhr.exhibitName === informExhibitInfoPage.exhibit_Name && nhr.operation !== 'activate_theme') {
        return {
          ...nhr,
          actions: nhr.actions.filter((a) => {
            return !((a.operation === 'add_attr' || a.operation === 'delete_attr') && a.content.key === payload.theKey);
          }),
        };
      }
      return nhr;
    });

    needHandleRules = mergeRules({
      oldRules: needHandleRules,
      exhibitName: informExhibitInfoPage.exhibit_Name,
      action: {
        operation: 'add_attr',
        content: {
          key: payload.theKey,
          value: payload.theValue,
          description: payload.theDescription,
        },
      },
    });
    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informExhibitInfoPage.node_ID,
      testRuleText: text,
    };
    const { data, errCode, ret, msg }: {
      data: boolean;
      errCode: number;
      msg: string;
      ret: number;
    } = yield call(FServiceAPI.InformalNode.createRules, params);

    if (ret !== 0 || errCode !== 0 || !data) {
      return fMessage(msg, 'error');
    }
    fMessage('自定义选项已更新');

    const params2: Parameters<typeof ruleMatchAndResult>[0] = {
      nodeID: informExhibitInfoPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      fMessage('规则匹配失败');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
      },
    });

    yield put<FetchInformalExhibitInfoAction>({
      type: 'fetchInformalExhibitInfo',
    });

  },
  * onClick_Side_Exhibit_EditDeleteAttr_DeleteBtn({ payload }: OnClick_Side_Exhibit_EditDeleteAttr_DeleteBtn_Action, {
    select,
    call,
    put,
  }: EffectsCommandMap) {
    const { informExhibitInfoPage }: ConnectState = yield select(({ informExhibitInfoPage }: ConnectState) => ({
      informExhibitInfoPage,
    }));

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informExhibitInfoPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    needHandleRules = needHandleRules.map((nhr) => {
      if (nhr.exhibitName === informExhibitInfoPage.exhibit_Name && nhr.operation !== 'activate_theme') {
        return {
          ...nhr,
          actions: nhr.actions.filter((a) => {
            return !(a.operation === 'add_attr' && a.content.key === payload.theKey);
          }),
        };
      }
      return nhr;
    });

    needHandleRules = mergeRules({
      oldRules: needHandleRules,
      exhibitName: informExhibitInfoPage.exhibit_Name,
      action: {
        operation: 'delete_attr',
        content: {
          key: payload.theKey,
        },
      },
    });
    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informExhibitInfoPage.node_ID,
      testRuleText: text,
    };
    const { data, errCode, ret, msg }: {
      data: boolean;
      errCode: number;
      msg: string;
      ret: number;
    } = yield call(FServiceAPI.InformalNode.createRules, params);

    if (ret !== 0 || errCode !== 0 || !data) {
      return fMessage(msg, 'error');
    }
    fMessage('自定义选项已删除');

    const params2: Parameters<typeof ruleMatchAndResult>[0] = {
      nodeID: informExhibitInfoPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      fMessage('规则匹配失败');
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
      },
    });

    yield put<FetchInformalExhibitInfoAction>({
      type: 'fetchInformalExhibitInfo',
    });
  },
};
